import { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Grid, Tooltip, Divider,
  Chip, FormControlLabel, Switch, Button,
} from '@mui/material';
import { Edit as EditIcon, PlaylistAdd as FillIcon, CloudDownload as TalanaIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { formatDate, formatDateRange } from '../formatDate';
import { parseAmount, parseRate, formatAmount, amountHint, implausibleAmountField } from '../parseAmount';
import { InfoTip } from '../LabelWithInfo';

const emptyValues = {
  uf: '', utm: '',
  minimumWage: '', pensionRate: '', healthRate: '', mutualRate: '', sisRate: '',
  unemploymentWorkerRate: '', unemploymentEmployerRate: '', lifeExpectancyRate: '',
  // Cesantía empleador contrato indefinido (OUT), fase 1b 2026-10-13 (default 2,4)
  unemploymentEmployerRateIndefinite: '',
  individualCapitalizationRate: '', protectedProfitRate: '', taxableCapUf: '',
  unemploymentCapUf: '', vatRate: '', operationalExpenses: '', marginMinimum: '',
  vacationProvisionFactor: '', gratificationRate: '', gratificationCap: '',
  // Mes parcial (decisión Erwin 2026-10-13): proporcionar los topes a los días
  // del período. Apagado = topes mensuales (comportamiento anterior).
  prorateCaps: false, prorateGratificationCap: false,
};

// Campos de tasas/topes: vacío = usar el default del motor de cálculo
const NORMATIVE_FIELDS = [
  'minimumWage', 'pensionRate', 'healthRate', 'mutualRate', 'sisRate',
  'unemploymentWorkerRate', 'unemploymentEmployerRate', 'unemploymentEmployerRateIndefinite',
  'lifeExpectancyRate', 'individualCapitalizationRate', 'protectedProfitRate', 'taxableCapUf',
  'unemploymentCapUf', 'vatRate', 'operationalExpenses', 'marginMinimum',
];

// Montos (UF, UTM, sueldo mínimo, gastos, topes, factores): formato chileno
// completo — "553.553" son miles, "41.057,2" decimal (parseAmount). Antes se
// usaba Number() y "553.553" se guardaba como 553,553 (bug 2026-09-29).
const toNumberOrNull = (v) => parseAmount(v);

// Campos de tasa: el usuario escribe PORCENTAJE (7 = 7%); se guarda decimal (0.07)
const RATE_FIELDS = [
  'pensionRate', 'healthRate', 'mutualRate', 'sisRate', 'unemploymentWorkerRate',
  'unemploymentEmployerRate', 'unemploymentEmployerRateIndefinite', 'lifeExpectancyRate', 'individualCapitalizationRate',
  'protectedProfitRate', 'vatRate', 'gratificationRate',
];

const percentToDecimal = (v) => {
  const n = parseRate(v); // tasas: solo decimal, nunca miles ("0,6" = "0.6")
  // toPrecision evita el arrastre binario (11,45 → 0.1145 exacto, no 0.11449999...)
  return n === null ? null : parseFloat((n / 100).toPrecision(12));
};

// Decimal guardado → porcentaje mostrado, sin arrastre binario (0.07 → "7")
const decimalToPercent = (v) =>
  v === null || v === undefined || v === '' ? '' : String(parseFloat((Number(v) * 100).toPrecision(12)));

const setToValues = (set) => ({
  uf: set.monthly?.uf ?? '',
  utm: set.monthly?.utm ?? '',
  ...Object.fromEntries(
    NORMATIVE_FIELDS.map((f) => [
      f,
      RATE_FIELDS.includes(f) ? decimalToPercent(set.normative?.[f]) : set.normative?.[f] ?? '',
    ])
  ),
  vacationProvisionFactor: set.legal?.vacationProvisionFactor ?? '',
  gratificationRate: decimalToPercent(set.legal?.gratificationRate),
  gratificationCap: set.legal?.gratificationCap ?? '',
  prorateCaps: set.normative?.prorateCaps === true,
  prorateGratificationCap: set.legal?.prorateGratificationCap === true,
});

const valuesToPayload = (values) => ({
  monthly: { uf: toNumberOrNull(values.uf), utm: toNumberOrNull(values.utm) },
  normative: {
    ...Object.fromEntries(
      NORMATIVE_FIELDS.map((f) => [
        f,
        RATE_FIELDS.includes(f) ? percentToDecimal(values[f]) : toNumberOrNull(values[f]),
      ])
    ),
    prorateCaps: values.prorateCaps === true,
  },
  legal: {
    vacationProvisionFactor: toNumberOrNull(values.vacationProvisionFactor),
    gratificationRate: percentToDecimal(values.gratificationRate),
    gratificationCap: toNumberOrNull(values.gratificationCap),
    prorateGratificationCap: values.prorateGratificationCap === true,
  },
});

/**
 * Parámetros del motor de cálculo por período (PLAN_ERP.md §4.8).
 * Decisión 2026-09-21 (Erwin): los períodos son los de TALANA (catálogo
 * erpTalanaPeriods, pestaña Talana); Amanda no crea períodos propios, solo
 * completa los parámetros (UF/UTM, normativos, legales) de cada período de
 * Talana. Un set referenciado por cotizaciones no se edita (el backend
 * responde PARAMETER_SET_REFERENCED): se completa el período siguiente.
 */
const ErpParameterSetsTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [rows, setRows] = useState([]); // { period, talana, parameterSet }
  const [talanaSynced, setTalanaSynced] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // uid o null (crear)
  const [period, setPeriod] = useState('');
  const [periodTalana, setPeriodTalana] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [taxBrackets, setTaxBrackets] = useState(null); // legal.taxBrackets (no editable en el form; viene de Talana o del set)
  // normative.pensionBaseRate (10% obligatorio) + normative.afpCommissions [{afp, comision}]:
  // el motor suma base + comisión de la AFP del trabajador; pensionRate queda como referencia
  const [afpInfo, setAfpInfo] = useState({ pensionBaseRate: null, afpCommissions: null });
  const [talanaResolved, setTalanaResolved] = useState(null); // { snapshot, warnings, resolvedAt, manualFields }
  const [fetchingTalana, setFetchingTalana] = useState(false);
  const [saving, setSaving] = useState(false);

  const sets = rows.filter((r) => r.parameterSet).map((r) => r.parameterSet);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/erp/parameter-sets/periods', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response?.status_response === 'OK') {
        setRows(response.data.response.data || []);
        setTalanaSynced(response.data.response.meta?.talanaSynced !== false);
      } else {
        setError(t('erp.parameters.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.parameters.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Completar los parámetros de un período de Talana que aún no los tiene:
  // se precarga con el set más reciente como base (mantención mensual)
  const openCreate = (row) => {
    setEditing(null);
    setPeriod(row.period);
    setPeriodTalana(row.talana);
    setValues(sets.length > 0 ? setToValues(sets[0]) : emptyValues);
    setTaxBrackets(sets.length > 0 ? sets[0].legal?.taxBrackets || null : null);
    setAfpInfo({
      pensionBaseRate: sets[0]?.normative?.pensionBaseRate ?? null,
      afpCommissions: sets[0]?.normative?.afpCommissions ?? null,
    });
    setTalanaResolved(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.parameterSet.uid);
    setPeriod(row.period);
    setPeriodTalana(row.talana);
    setValues(setToValues(row.parameterSet));
    setTaxBrackets(row.parameterSet.legal?.taxBrackets || null);
    setAfpInfo({
      pensionBaseRate: row.parameterSet.normative?.pensionBaseRate ?? null,
      afpCommissions: row.parameterSet.normative?.afpCommissions ?? null,
    });
    setTalanaResolved(row.parameterSet.talanaResolved || null);
    setDialogOpen(true);
  };

  // "Traer de Talana": resuelve las variables con vigencia a la fecha del
  // período y pre-llena el formulario (todo sigue editable). Guarda el
  // snapshot para enviarlo con el set (trazabilidad).
  const fetchFromTalana = async () => {
    try {
      setFetchingTalana(true);
      const response = await api.get('/erp/parameter-sets/resolve', {
        params: { organizationId: organization, period },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response.data.response?.data;
      if (response.data.response?.status_response !== 'OK' || !data) {
        showAlert(t('erp.parameters.talana.fetchError'), 'error');
        return;
      }
      const resolved = data.values || {};
      setValues((prev) => {
        const next = { ...prev };
        Object.entries(resolved.normative || {}).forEach(([k, v]) => {
          if (v === null || v === undefined) return;
          next[k] = RATE_FIELDS.includes(k) ? decimalToPercent(v) : String(v);
        });
        if (resolved.legal?.gratificationCap !== undefined && resolved.legal.gratificationCap !== null) {
          next.gratificationCap = String(resolved.legal.gratificationCap);
        }
        return next;
      });
      if (Array.isArray(resolved.legal?.taxBrackets) && resolved.legal.taxBrackets.length > 0) {
        setTaxBrackets(resolved.legal.taxBrackets);
      }
      if (resolved.normative?.pensionBaseRate !== undefined) {
        setAfpInfo({
          pensionBaseRate: resolved.normative.pensionBaseRate ?? null,
          afpCommissions: resolved.normative.afpCommissions ?? null,
        });
      }
      setTalanaResolved({
        snapshot: data.snapshot || {},
        warnings: data.warnings || [],
        manualFields: data.manualFields || [],
        resolvedAt: data.resolvedAt,
        period: data.period,
      });
      showAlert(t('erp.parameters.talana.fetched', { count: Object.keys(data.snapshot || {}).length }), 'success');
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      if (code === 'PARAMETERS_NOT_SYNCED') showAlert(t('erp.parameters.talana.paramsNotSynced'), 'warning');
      else if (code === 'PERIOD_NOT_IN_TALANA') showAlert(t('erp.parameters.talana.notInTalana'), 'error');
      else showAlert(t('erp.parameters.talana.fetchError'), 'error');
    } finally {
      setFetchingTalana(false);
    }
  };

  // Por defecto: períodos abiertos en Talana + los que ya tienen parámetros
  const visibleRows = rows.filter((r) => showClosed || r.parameterSet || (r.talana && !r.talana.cerrado));

  const talanaChip = (row) => {
    if (!row.talana) {
      return <Chip size="small" color="warning" variant="outlined" label={t('erp.parameters.talana.missing')} />;
    }
    const dates = row.talana.desde && row.talana.hasta ? formatDateRange(row.talana.desde, row.talana.hasta) : '';
    return (
      <Tooltip title={dates}>
        <Chip size="small" variant="outlined"
          color={row.talana.cerrado ? 'default' : 'success'}
          label={row.talana.cerrado ? t('erp.parameters.talana.closed') : t('erp.parameters.talana.open')} />
      </Tooltip>
    );
  };

  const handleSave = async () => {
    if (!period.match(/^\d{4}-\d{2}$/)) {
      showAlert(t('erp.parameters.invalidPeriod'), 'warning');
      return;
    }

    const base = valuesToPayload(values);
    // Un sueldo mínimo de 553 o una UTM de 71 solo pueden venir de un punto de
    // miles leído como decimal: no se guarda.
    const implausible = implausibleAmountField({ ...base.monthly, ...base.normative });
    if (implausible) {
      showAlert(t('erp.parameters.implausibleAmount', { field: t(`erp.parameters.fields.${implausible}`), value: values[implausible] }), 'error');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        organizationId: organization,
        period,
        ...base,
        normative: { ...base.normative, pensionBaseRate: afpInfo.pensionBaseRate ?? null, afpCommissions: afpInfo.afpCommissions ?? null },
        legal: { ...base.legal, taxBrackets: taxBrackets || null },
        talanaResolved: talanaResolved
          ? { snapshot: talanaResolved.snapshot, warnings: talanaResolved.warnings, resolvedAt: talanaResolved.resolvedAt, period: talanaResolved.period }
          : null,
      };

      const response = editing
        ? await api.put(`/erp/parameter-sets/${editing}`, payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        : await api.post('/erp/parameter-sets', payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          });

      if (response.data.response?.status_response === 'OK') {
        // Corrección de un set referenciado (super-admin): cotizaciones que
        // perdieron su simulación y las en uso que quedaron intactas.
        const correction = response.data.response?.correction;
        if (correction && (correction.invalidated?.length || correction.untouched?.length)) {
          const parts = [];
          if (correction.invalidated?.length) parts.push(t('erp.parameters.correction.invalidated', { count: correction.invalidated.length, list: correction.invalidated.join(', ') }));
          if (correction.untouched?.length) parts.push(t('erp.parameters.correction.untouched', { count: correction.untouched.length, list: correction.untouched.join(', ') }));
          showAlert(`${t('erp.parameters.saveSuccess')} ${parts.join(' ')}`, 'warning');
        } else {
          showAlert(t('erp.parameters.saveSuccess'), 'success');
        }
        setDialogOpen(false);
        load();
      } else {
        showAlert(t('erp.parameters.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      const message = err.response?.data?.errorMessage || '';
      if (code === 'PARAMETER_SET_REFERENCED') {
        showAlert(t('erp.parameters.referencedError'), 'error');
      } else if (code === 'PERIOD_NOT_IN_TALANA') {
        showAlert(t('erp.parameters.talana.notInTalana'), 'error');
      } else if (message.includes('already exists')) {
        showAlert(t('erp.parameters.duplicatePeriod'), 'error');
      } else {
        showAlert(t('erp.parameters.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const snapshotFor = (key) => talanaResolved?.snapshot?.[key] || null;

  const numberField = (key, labelKey) => {
    const snap = snapshotFor(key);
    return (
      <TextField
        fullWidth
        size="small"
        inputProps={{ inputMode: 'decimal' }}
        label={t(labelKey)}
        value={values[key]}
        onChange={(e) => setValues((p) => ({ ...p, [key]: e.target.value }))}
        helperText={[
          snap ? t('erp.parameters.talana.fromTalana', { code: snap.code, desde: snap.desde ? formatDate(snap.desde) : '—' }) : null,
          RATE_FIELDS.includes(key) ? null : amountHint(values[key]) || null,
        ].filter(Boolean).join(' · ') || undefined}
        FormHelperTextProps={snap ? { sx: { color: '#2E7D32' } } : undefined}
      />
    );
  };

  const warningLabel = (w) =>
    `${t(`erp.parameters.fields.${w.field}`, { defaultValue: w.field })}: ${
      w.type === 'not_found' ? t('erp.parameters.talana.warningNotFound') : t('erp.parameters.talana.warningChanges')
    }`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>{t('erp.parameters.title')}</Typography>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Switch size="small" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />}
          label={<Typography variant="body2">{t('erp.parameters.talana.showClosed')}</Typography>}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('erp.parameters.subtitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !talanaSynced && <Alert severity="warning" sx={{ mb: 2 }}>{t('erp.parameters.talana.notSynced')}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : visibleRows.length === 0 ? (
        <Alert severity="info">{t('erp.parameters.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.parameters.table.period')}</TableCell>
                <TableCell>{t('erp.parameters.table.talana')}</TableCell>
                <TableCell>UF</TableCell>
                <TableCell>UTM</TableCell>
                <TableCell>{t('erp.parameters.fields.minimumWage')}</TableCell>
                <TableCell>IVA %</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => {
                const set = row.parameterSet;
                return (
                  <TableRow key={row.period} hover sx={!set ? { '& td': { color: 'text.secondary' } } : undefined}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.period}
                      {row.talana?.desde && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatDateRange(row.talana.desde, row.talana.hasta)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{talanaChip(row)}</TableCell>
                    <TableCell>{formatAmount(set?.monthly?.uf) || '—'}</TableCell>
                    <TableCell>{formatAmount(set?.monthly?.utm) || '—'}</TableCell>
                    <TableCell>{formatAmount(set?.normative?.minimumWage) || '—'}</TableCell>
                    <TableCell>{set?.normative?.vatRate ?? '—'}</TableCell>
                    <TableCell align="right">
                      {set ? (
                        <Tooltip title={t('erp.parameters.edit')}>
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Button size="small" startIcon={<FillIcon />} onClick={() => openCreate(row)}
                          sx={{ textTransform: 'none', color: '#6A5AF9' }}>
                          {t('erp.parameters.talana.fill')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth={false}
        sx={{ '& .MuiDialog-paper': { width: '60vw', minWidth: 700, maxWidth: 1500 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            {editing ? t('erp.parameters.edit') : t('erp.parameters.talana.fill')} · {period}
            {periodTalana?.desde && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Talana: {formatDateRange(periodTalana.desde, periodTalana.hasta)}
                {periodTalana.cerrado ? ` · ${t('erp.parameters.talana.closed')}` : ` · ${t('erp.parameters.talana.open')}`}
              </Typography>
            )}
          </Box>
          {periodTalana && (
            <Button
              variant="outlined"
              size="small"
              startIcon={fetchingTalana ? <CircularProgress size={14} /> : <TalanaIcon />}
              disabled={fetchingTalana}
              onClick={fetchFromTalana}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {fetchingTalana ? t('erp.parameters.talana.fetching') : t('erp.parameters.talana.fetch')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {talanaResolved && (
            <Alert severity={talanaResolved.warnings?.length ? 'warning' : 'success'} sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="body2">
                {t('erp.parameters.talana.resolvedSummary', {
                  count: Object.keys(talanaResolved.snapshot || {}).length,
                  brackets: taxBrackets ? taxBrackets.length : 0,
                })}
                {' '}{t('erp.parameters.talana.manualHint')}
              </Typography>
              {(talanaResolved.warnings || []).map((w) => (
                <Typography key={`${w.field}-${w.type}`} variant="caption" sx={{ display: 'block' }}>• {warningLabel(w)}</Typography>
              ))}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('erp.parameters.sections.monthly')}<InfoTip title={t('erp.tooltips.parameters.monthly')} /></Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={6} sm={3}>{numberField('uf', 'erp.parameters.fields.uf')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('utm', 'erp.parameters.fields.utm')}</Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('erp.parameters.sections.normative')}<InfoTip title={t('erp.tooltips.parameters.normative')} /></Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0 }}>{t('erp.parameters.defaultsHint')}</Alert>
            </Grid>
            <Grid item xs={6} sm={3}>{numberField('minimumWage', 'erp.parameters.fields.minimumWage')}</Grid>
            <Grid item xs={6} sm={3}>
              {numberField('pensionRate', 'erp.parameters.fields.pensionRate')}
              {Array.isArray(afpInfo.afpCommissions) && afpInfo.afpCommissions.length > 0 && (
                <Tooltip title={afpInfo.afpCommissions.map((c) => `${c.afp}: ${decimalToPercent(afpInfo.pensionBaseRate ?? 0.1)}% + ${decimalToPercent(c.comision)}% = ${decimalToPercent((afpInfo.pensionBaseRate ?? 0.1) + c.comision)}%`).join(' · ')}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, cursor: 'help' }}>
                    {t('erp.parameters.talana.afpCommissions', { base: decimalToPercent(afpInfo.pensionBaseRate ?? 0.1), count: afpInfo.afpCommissions.length })}
                  </Typography>
                </Tooltip>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>{numberField('healthRate', 'erp.parameters.fields.healthRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('mutualRate', 'erp.parameters.fields.mutualRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('sisRate', 'erp.parameters.fields.sisRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('unemploymentWorkerRate', 'erp.parameters.fields.unemploymentWorkerRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('unemploymentEmployerRate', 'erp.parameters.fields.unemploymentEmployerRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('unemploymentEmployerRateIndefinite', 'erp.parameters.fields.unemploymentEmployerRateIndefinite')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('lifeExpectancyRate', 'erp.parameters.fields.lifeExpectancyRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('individualCapitalizationRate', 'erp.parameters.fields.individualCapitalizationRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('protectedProfitRate', 'erp.parameters.fields.protectedProfitRate')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('taxableCapUf', 'erp.parameters.fields.taxableCapUf')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('unemploymentCapUf', 'erp.parameters.fields.unemploymentCapUf')}</Grid>
            {/* Gastos operacionales: desde la fase 4 del cotizador OUT/EST
                (2026-10-13) van en el acuerdo comercial. El valor del set se
                conserva al guardar (lo usan las cotizaciones anteriores), pero
                ya no se edita aquí. */}
            <Grid item xs={6} sm={3}>{numberField('marginMinimum', 'erp.parameters.fields.marginMinimum')}</Grid>
            <Grid item xs={6} sm={3}>{numberField('vatRate', 'erp.parameters.fields.vatRate')}</Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">{t('erp.parameters.operationalExpensesMoved')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('erp.parameters.sections.legal')}<InfoTip title={t('erp.tooltips.parameters.legal')} /></Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={6} sm={4}>{numberField('vacationProvisionFactor', 'erp.parameters.fields.vacationProvisionFactor')}</Grid>
            <Grid item xs={6} sm={4}>{numberField('gratificationRate', 'erp.parameters.fields.gratificationRate')}</Grid>
            <Grid item xs={6} sm={4}>{numberField('gratificationCap', 'erp.parameters.fields.gratificationCap')}</Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('erp.parameters.sections.partialMonth')}<InfoTip title={t('erp.tooltips.parameters.partialMonth')} /></Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={values.prorateCaps === true}
                  onChange={(e) => setValues((p) => ({ ...p, prorateCaps: e.target.checked }))} />}
                label={<Typography variant="body2">{t('erp.parameters.fields.prorateCaps')}</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={values.prorateGratificationCap === true}
                  onChange={(e) => setValues((p) => ({ ...p, prorateGratificationCap: e.target.checked }))} />}
                label={<Typography variant="body2">{t('erp.parameters.fields.prorateGratificationCap')}</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
          >
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpParameterSetsTab;
